import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {                                                             
  Box,
  Button,
  Container,
  FormControl,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TextField,
  MenuItem,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

import bg from "../img/splashSceen.png";
import companylogo from "../img/red with white bck.png";

import {
  fetchDailyOrder,
  createDailyOrder,
  updateDailyOrder,
  clearCurrentOrder,
  fetchClientsWithOrderStatus,
} from "../store/features/client/dailyCateringOrderSlice";
import useDebouncedValue from "../hooks/useDebouncedValue";
import OrderFormSelectors from "../components/DailyCateringOrder/OrderFormSelectors";
import ServiceCountsGrid from "../components/DailyCateringOrder/ServiceCountsGrid";
import LayoutOrderTree from "../components/DailyCateringOrder/LayoutOrderTree";
import OrderDetailsToggles from "../components/DailyCateringOrder/OrderDetailsToggles";
import OrderFormSkeleton from "../components/DailyCateringOrder/OrderFormSkeleton";
import YesNoToggle from "../components/DailyCateringOrder/YesNoToggle";
import { buildCountsFromEntries, flattenCountsToEntries, getLeafKeys } from "../utility/layoutTree";

const localTheme = createTheme({
  palette: {
    primary: { main: "#c60800" },
    secondary: { main: "#c60800" },
  },
});

const getTodayString = () => new Date().toISOString().split("T")[0];
const DRAFT_KEY = "dailyCateringOrderDraft";
function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function DailyCateringOrderForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const today = getTodayString();
  const [searchParams] = useSearchParams();

  const {
    current: existingOrder,
    isToday,
    loading: orderLoading,
    saving,
    clientsWithStatus,
    clientsWithStatusLoading: clientsLoading,
    branchSummary,
  } = useSelector((state) => state.dailyCateringOrder);
  const clients = clientsWithStatus;

  // Read once on mount — later renders should not re-read localStorage.
  const [draft] = useState(loadDraft);
  // Guards the "no existing order" reset so restoring a draft on mount
  // doesn't immediately get wiped once fetchDailyOrder resolves with null.
  const draftAppliedRef = useRef(false);

  const [date, setDate] = useState(draft?.date ?? today);
  const [cluster, setCluster] = useState(draft?.cluster ?? "cluster1");
  const [clientId, setClientId] = useState(draft?.clientId ?? "");
  const [vendorId, setVendorId] = useState(draft?.vendorId ?? "");
  const [serviceCounts, setServiceCounts] = useState(draft?.serviceCounts ?? {}); // { [serviceName]: count }
  const [layoutCounts, setLayoutCounts] = useState(draft?.layoutCounts ?? {}); // { [dot.joined.key]: count } — advanced clients only
  const [branchStatuses, setBranchStatuses] = useState(draft?.branchStatuses ?? {}); // { [branchKey]: { menu3pm?, orderSent?, orderReceived? } } — only fields the user touched this session
  const [branchKey, setBranchKey] = useState(draft?.branchKey ?? ""); // selected top-level "site" — advanced clients only
  const [menu3pm, setMenu3pm] = useState(draft?.menu3pm ?? true);
  const [orderSent, setOrderSent] = useState(draft?.orderSent ?? true);
  const [orderReceived, setOrderReceived] = useState(draft?.orderReceived ?? true);
  const [active, setActive] = useState(draft?.active ?? true);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });
  const debouncedDate = useDebouncedValue(date, 400);
  useEffect(() => {
    if (debouncedDate) dispatch(fetchClientsWithOrderStatus(debouncedDate));
  }, [debouncedDate, dispatch]);

  const orderPlacedByClientId = useMemo(() => {
    const map = {};
    clientsWithStatus.forEach((c) => {
      map[c._id] = !!c.orderPlaced;
    });
    return map;
  }, [clientsWithStatus]);

  const companyFilter = searchParams.get("company");

  const visibleClients = useMemo(() => {
    const targetCluster = companyFilter || cluster;
    return clients.filter((c) => c.cluster === targetCluster);
  }, [clients, companyFilter, cluster]);

  const selectedClient = useMemo(
    () => clients.find((c) => c._id === clientId) || null,
    [clients, clientId],
  );

  const vendorOptions = selectedClient?.vendor || [];
  const clientServices = selectedClient?.services || [];
  const layoutType = selectedClient?.layoutType || "simple";
  const clientLayout = selectedClient?.layout || [];

  // The selected "site" (a top-level layout node) — advanced clients only.
  const selectedBranch = clientLayout.find((n) => n.id === branchKey) || null;
  const selectedBranchSummary = (branchSummary || []).find((b) => b.key === branchKey) || null;
  // If the site itself is a group (e.g. "Plant 1"), render its children
  // directly instead of re-wrapping the whole site in its own accordion.
  const selectedBranchNodes = selectedBranch
    ? selectedBranch.children && selectedBranch.children.length > 0
      ? selectedBranch.children
      : [selectedBranch]
    : [];
  const selectedBranchParentKey =
    selectedBranch && selectedBranch.children && selectedBranch.children.length > 0
      ? selectedBranch.id
      : "";

  const editable = true;
  // When a company is marked inactive, its counts/toggles are locked —
  // only the Active toggle itself stays editable so it can be turned back on.
  const fieldsEditable = editable && active !== false;
  // Past dates with nothing recorded have no data to show — hide the
  // toggles/counts entirely instead of rendering an empty, uneditable form.
  const hasData = editable || !!existingOrder;
  const resetServiceCounts = (services) => {
    const blank = {};
    services.forEach((s) => {
      blank[s] = 0;
    });
    setServiceCounts(blank);
  };

  const resetLayoutCounts = (layout) => {
    const blank = {};
    getLeafKeys(layout).forEach((key) => {
      blank[key] = 0;
    });
    setLayoutCounts(blank);
  };

  const handleClientChange = (e) => {
    const newClientId = e.target.value;
    setClientId(newClientId);
    dispatch(clearCurrentOrder());
    const client = clients.find((c) => c._id === newClientId);
    const newVendorOptions = client?.vendor || [];
    setVendorId(newVendorOptions.length === 1 ? newVendorOptions[0]._id : "");
    if ((client?.layoutType || "simple") === "advanced") {
      resetLayoutCounts(client?.layout || []);
    } else {
      resetServiceCounts(client?.services || []);
    }
    setBranchStatuses({});
    setBranchKey("");
    setMenu3pm(true);
    setOrderSent(true);
    setOrderReceived(true);
    setActive(client?.active !== undefined ? !!client.active : true);
  };

  const handleBranchKeyChange = (e) => {
    setBranchKey(e.target.value);
  };

  const handleActiveChange = (value) => {
    setActive(value);
    if (!value) {
      setMenu3pm(false);
      setOrderSent(false);
      setOrderReceived(false);
      if (layoutType === "advanced") {
        resetLayoutCounts(clientLayout);
        const forcedOff = {};
        clientLayout.forEach((node) => {
          forcedOff[node.id] = { menu3pm: false, orderSent: false, orderReceived: false };
        });
        setBranchStatuses(forcedOff);
      } else {
        resetServiceCounts(clientServices);
      }
    }
  };

  const handleVendorChange = (e) => {
    setVendorId(e.target.value);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;

    setDate(newDate);
    dispatch(clearCurrentOrder());
  };

  const handleClusterChange = (e) => {
    setCluster(e.target.value);
    setClientId("");
    setVendorId("");
    setServiceCounts({});
    setLayoutCounts({});
    setBranchStatuses({});
    setBranchKey("");
    dispatch(clearCurrentOrder());
  };

  // Whenever date + client + vendor are all chosen, look up whatever's
  // already saved so it can be shown (editable if today, read-only otherwise).
  useEffect(() => {
    if (debouncedDate && clientId && vendorId) {
      dispatch(fetchDailyOrder({ date: debouncedDate, clientId, vendorId }));
    }
  }, [debouncedDate, clientId, vendorId, dispatch]);


  useEffect(() => {
    if (!clientId || !vendorId) return;

    setBranchStatuses({});

    if (existingOrder) {
      if (layoutType === "advanced") {
        setLayoutCounts(buildCountsFromEntries(clientLayout, existingOrder.layoutEntries));
      } else {
        const counts = {};
        clientServices.forEach((s) => {
          const match = existingOrder.services?.find((svc) => svc.name === s);
          counts[s] = match ? match.count : 0;
        });
        setServiceCounts(counts);
      }
      setMenu3pm(!!existingOrder.menu3pm);
      setOrderSent(!!existingOrder.orderSent);
      setOrderReceived(!!existingOrder.orderReceived);
      setActive(
        existingOrder.active !== undefined ? !!existingOrder.active : true,
      );
      draftAppliedRef.current = true;
    } else if (
      !draftAppliedRef.current &&
      draft &&
      draft.date === date &&
      draft.clientId === clientId &&
      draft.vendorId === vendorId
    ) {
      // Nothing saved yet for this combination, but it matches the draft
      // restored on mount — keep those values instead of blanking them.
      draftAppliedRef.current = true;
    } else {
      if (layoutType === "advanced") {
        resetLayoutCounts(clientLayout);
      } else {
        resetServiceCounts(clientServices);
      }
      setMenu3pm(true);
      setOrderSent(true);
      setOrderReceived(true);
      setActive(
        selectedClient?.active !== undefined ? !!selectedClient.active : true,
      );
    }
  }, [existingOrder]);

  
  useEffect(() => {
    const draftData = {
      date,
      cluster,
      clientId,
      vendorId,
      serviceCounts,
      layoutCounts,
      branchStatuses,
      branchKey,
      menu3pm,
      orderSent,
      orderReceived,
      active,
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    } catch {

    }
  }, [date, cluster, clientId, vendorId, serviceCounts, layoutCounts, branchStatuses, branchKey, menu3pm, orderSent, orderReceived, active]);

  // "Additional Orders" is a free-text note field rather than a headcount —
  // every other service/leaf stays a numeric-only count.
  const isFreeTextService = (name) => name?.trim().toLowerCase() === "additional orders";
  const isFreeTextLayoutKey = (key) => key.split(".").pop() === "additional-orders";

  const handleCountChange = (serviceName, value) => {
    if (isFreeTextService(serviceName)) {
      setServiceCounts((prev) => ({ ...prev, [serviceName]: value }));
      return;
    }
    if (!/^\d*$/.test(value)) return;
    setServiceCounts((prev) => ({
      ...prev,
      [serviceName]: value === "" ? "" : Number(value),
    }));
  };

  const handleLayoutCountChange = (key, value) => {
    if (isFreeTextLayoutKey(key)) {
      setLayoutCounts((prev) => ({ ...prev, [key]: value }));
      return;
    }
    if (!/^\d*$/.test(value)) return;
    setLayoutCounts((prev) => ({
      ...prev,
      [key]: value === "" ? "" : Number(value),
    }));
  };

  const buildBranchStatusesPayload = () => {
    return (branchSummary || []).map((b) => ({
      key: b.key,
      menu3pm: branchStatuses[b.key]?.menu3pm ?? !!b.menu3pm,
      orderSent: branchStatuses[b.key]?.orderSent ?? !!b.orderSent,
      orderReceived: branchStatuses[b.key]?.orderReceived ?? !!b.orderReceived,
    }));
  };

  const handleBranchStatusChange = (branchKey, field, value) => {
    setBranchStatuses((prev) => ({
      ...prev,
      [branchKey]: { ...(prev[branchKey] || {}), [field]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) return showSnackbar("Please select a company.", "error");
    if (!vendorId) return showSnackbar("Please select a vendor.", "error");
    if (layoutType === "advanced" && !branchKey)
      return showSnackbar("Please select a site.", "error");

    const vendor = vendorOptions.find((v) => v._id === vendorId);

    const payload = {
      date,
      client: clientId,
      clientName: selectedClient?.name || "",
      vendorId,
      vendorName: vendor?.name || "",
      ...(layoutType === "advanced"
        ? {
            services: [],
            layoutEntries: flattenCountsToEntries(clientLayout, layoutCounts),
            branchStatuses: buildBranchStatusesPayload(),
          }
        : {
            services: clientServices.map((s) => ({
              name: s,
              count: isFreeTextService(s)
                ? serviceCounts[s] ?? ""
                : Number(serviceCounts[s]) || 0,
            })),
            menu3pm,
            orderSent,
            orderReceived,
          }),
      active,
    };

    try {
      if (existingOrder?._id) {
        await dispatch(
          updateDailyOrder({ id: existingOrder._id, data: payload }),
        ).unwrap();
        showSnackbar("Order updated successfully!", "success");
      } else {
        await dispatch(createDailyOrder(payload)).unwrap();
        showSnackbar("Order saved successfully!", "success");
      }
      if (date) dispatch(fetchClientsWithOrderStatus(date));
      setClientId("");
      setVendorId("");
      setServiceCounts({});
      setLayoutCounts({});
      setBranchStatuses({});
      setBranchKey("");
      setMenu3pm(true);
      setOrderSent(true);
      setOrderReceived(true);
      setActive(true);
      dispatch(clearCurrentOrder());
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        // Storage unavailable — nothing to clean up.
      }
    } catch (err) {
      showSnackbar(err?.message || "Failed to save order.", "error");
    }
  };

  return (
    <ThemeProvider theme={localTheme}>
      <CssBaseline />
      <Box
        sx={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "fit",
          backgroundPosition: "center",
          minHeight: "100vh",
          py: 5,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2.5, sm: 4 },
              mt: { xs: 2, sm: 5 },
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={companylogo}
                alt="logo"
                sx={{ width: { xs: 150, sm: 200 }, mb: 1 }}
              />
            </Box>

            <Typography
              variant="h5"
              align="center"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "1.15rem", sm: "1.5rem" } }}
            >
              DAILY CATERING ORDER FORM
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              < OrderFormSelectors
                date={date}
                onDateChange={handleDateChange}
                cluster={cluster}
                onClusterChange={handleClusterChange}
                clientId={clientId}
                onClientChange={handleClientChange}
                clients={visibleClients}
                clientsLoading={clientsLoading}
                orderPlacedByClientId={orderPlacedByClientId}
                vendorId={vendorId}
                onVendorChange={handleVendorChange}
                vendorOptions={vendorOptions}
             />

        

              {orderLoading && (
                <OrderFormSkeleton
                  serviceCount={
                    layoutType === "advanced"
                      ? getLeafKeys(clientLayout).length
                      : clientServices.length
                  }
                />
              )}

              {clientId && vendorId && !orderLoading && (
                <>
                  {hasData && (
                    <>
                      <FormControl fullWidth sx={{ mt: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: { xs: 1, sm: 0 },
                          }}
                        >
                          <YesNoToggle
                            label="Active"
                            value={active}
                            onChange={handleActiveChange}
                            disabled={!editable}
                            onLabel="ON"
                            offLabel="OFF"
                          />
                        </Box>
                      </FormControl>

                      {layoutType === "advanced" ? (
                        <>
                          <FormControl fullWidth sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">
                              Site <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={branchKey}
                              onChange={handleBranchKeyChange}
                            >
                              <MenuItem value="">
                                <em>Select site</em>
                              </MenuItem>
                              {clientLayout.map((node) => (
                                <MenuItem key={node.id} value={node.id}>
                                  {node.title}
                                </MenuItem>
                              ))}
                            </TextField>
                          </FormControl>

                          {branchKey && (
                            <>
                              <FormControl fullWidth sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Service Counts
                                </Typography>
                                <LayoutOrderTree
                                  nodes={selectedBranchNodes}
                                  counts={layoutCounts}
                                  onCountChange={handleLayoutCountChange}
                                  editable={fieldsEditable}
                                  parentKey={selectedBranchParentKey}
                                />
                              </FormControl>

                              <OrderDetailsToggles
                                menu3pm={branchStatuses[branchKey]?.menu3pm ?? !!selectedBranchSummary?.menu3pm}
                                onMenu3pmChange={(v) => handleBranchStatusChange(branchKey, "menu3pm", v)}
                                orderSent={branchStatuses[branchKey]?.orderSent ?? !!selectedBranchSummary?.orderSent}
                                onOrderSentChange={(v) => handleBranchStatusChange(branchKey, "orderSent", v)}
                                orderReceived={
                                  branchStatuses[branchKey]?.orderReceived ?? !!selectedBranchSummary?.orderReceived
                                }
                                onOrderReceivedChange={(v) => handleBranchStatusChange(branchKey, "orderReceived", v)}
                                editable={fieldsEditable}
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <ServiceCountsGrid
                            services={clientServices}
                            counts={serviceCounts}
                            onCountChange={handleCountChange}
                            editable={fieldsEditable}
                          />

                          <OrderDetailsToggles
                            menu3pm={menu3pm}
                            onMenu3pmChange={setMenu3pm}
                            orderSent={orderSent}
                            onOrderSentChange={setOrderSent}
                            orderReceived={orderReceived}
                            onOrderReceivedChange={setOrderReceived}
                            editable={fieldsEditable}
                          />
                        </>
                      )}
                    </>
                  )}

                  {editable ? (
                    <FormControl fullWidth sx={{ mt: 3 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={saving}
                        startIcon={
                          saving && (
                            <CircularProgress size={20} color="inherit" />
                          )
                        }
                      >
                        {saving
                          ? "Saving..."
                          : existingOrder?._id
                            ? "Update Order"
                            : "Save and Next"}
                      </Button>
                    </FormControl>
                  ) : (
                    !existingOrder && (
                      <Typography
                        sx={{ mt: 3 }}
                        color="text.secondary"
                        align="center"
                      >
                        No order was recorded for this date.
                      </Typography>
                    )
                  )}
                </>
              )}
                     <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  sx={{
                    my:2
                  }}
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={() => navigate("/daily-catering-order-report")}
                >
                  View Report
                </Button>

              <Box sx={{ textAlign: "center", mt: 5, color: "text.secondary" }}>
                <Typography variant="body2">
                  Powered by{" "}
                  <Typography
                    component="span"
                    color="primary"
                    fontWeight="bold"
                  >
                    HOGIST
                  </Typography>{" "}
                  © {new Date().getFullYear()} All Rights Reserved
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
